import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth, API } from '../App';
import { t } from '../i18n';
import { Download, FileBarChart2 } from 'lucide-react';

export default function ROIReportPage() {
  const { lang } = useAuth();
  const [r, setR] = useState(null);
  useEffect(() => { axios.get(`${API}/roi/export`).then(res => setR(res.data)); }, []);

  if (!r) return <div className="skeleton h-64 rounded-sm" />;

  const download = () => {
    const blob = new Blob([JSON.stringify(r, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `roi-${r.report_generated_at.slice(0, 10)}.json`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="fade-up max-w-4xl" data-testid="roi-page">
      <div className="flex justify-between items-end mb-6">
        <div>
          <div className="data-label accent-line text-[#DDA079] mb-3">{t(lang, 'nav.admin_roi')}</div>
          <h1 className="font-headings text-4xl text-white">Monthly ROI Report</h1>
        </div>
        <button data-testid="roi-export" onClick={download} className="btn-primary flex items-center gap-2">
          <Download size={14} /> Export Report
        </button>
      </div>

      <div className="v-card p-6 mb-6 border-[#C17F59]/30 bg-[#C17F59]/[0.04]">
        <div className="flex items-start gap-3">
          <FileBarChart2 size={18} className="text-[#C17F59] mt-1" />
          <div>
            <div className="data-label mb-2">Baseline assumption (explicit)</div>
            <p className="text-slate-200 text-[14px] leading-relaxed">{r.baseline_assumption}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <Tile label="Queries (30d)" value={r.metrics.queries_total} />
        <Tile label="Free queries %" value={`${r.metrics.free_queries_pct}%`} accent="text-emerald-400" />
        <Tile label="AI spend (30d)" value={`₹${r.metrics.ai_spend_inr.toFixed(2)}`} accent="text-[#DDA079]" />
        <Tile label="Savings vs MS Copilot" value={`₹${(r.metrics.savings_vs_baseline_inr / 100000).toFixed(2)}L`} accent="text-emerald-400" />
      </div>

      <div className="v-card p-6 mb-6">
        <div className="data-label mb-3">Cost by model (30d)</div>
        <table className="w-full text-[13px]">
          <thead>
            <tr><th className="data-label text-left pb-2">Model</th><th className="data-label text-right pb-2">Spend (₹)</th></tr>
          </thead>
          <tbody>
            {Object.entries(r.metrics.cost_by_model_inr).map(([m, v]) => (
              <tr key={m} className="border-t border-white/5">
                <td className="py-2 text-white">{m}</td>
                <td className="py-2 text-right font-mono text-white">{v.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="v-card p-6">
        <div className="data-label mb-3">Per-department breakdown</div>
        <table className="w-full text-[13px]">
          <thead>
            <tr>
              <th className="data-label text-left pb-2">Department</th>
              <th className="data-label text-right pb-2">Queries</th>
              <th className="data-label text-right pb-2">Free %</th>
              <th className="data-label text-right pb-2">Spend (₹)</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(r.metrics.per_department).map(([d, v]) => (
              <tr key={d} className="border-t border-white/5">
                <td className="py-2 text-white">{d}</td>
                <td className="py-2 text-right font-mono">{v.queries}</td>
                <td className="py-2 text-right font-mono text-emerald-400">{v.free_pct}%</td>
                <td className="py-2 text-right font-mono">{v.spend_inr.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-[11px] text-slate-500 font-mono mt-4 text-right">
        Generated {new Date(r.report_generated_at).toLocaleString('en-IN')}
      </div>
    </div>
  );
}

function Tile({ label, value, accent = 'text-white' }) {
  return (
    <div className="v-card p-5">
      <div className="data-label">{label}</div>
      <div className={`font-mono text-2xl mt-2 ${accent}`}>{value}</div>
    </div>
  );
}
